/**
 * The link window (slice 2e spec §6.2; Paper 61–63): a dialog on a desktop, a
 * sheet from the bottom on a phone.
 *
 * 1. Your number: the phone field, its country the workspace timezone's.
 * 2. Text the code: the code, the line, **Text the code** (an `sms:` link, the
 *    primary action on a phone and a desktop), the countdown; on a desktop a
 *    QR code of the same link. It waits on the
 *    `messaging` hub event, which refetches the page's data. A code from
 *    another handle swaps the countdown for "Is that you?" (§6.1); a code
 *    past its 10 minutes offers New code.
 * 3. Linked: the contact card as Messages shows it, and the one last tap.
 *
 * The step comes from the page's data for the link the window made
 * (`windowStep`); only the number and the code (`heldCodes`) are its own.
 */
import { Button, buttonVariants } from "@crew/ui/components/button";
import { Input } from "@crew/ui/components/input";
import { useIsMobile } from "@crew/ui/hooks/use-mobile";
import { cn } from "@crew/ui/lib/utils";
import { ChevronDown } from "lucide-react";
import { useState, type FormEvent } from "react";
import { BIG } from "./window-kit";
import { Footer, Head, WindowShell } from "./window-parts";
import { useNavigate } from "react-router-dom";
import { errorHint } from "../../app/api";
import { COUNTRIES, type Country } from "../../../worker/messaging/phone";
import {
  useConfirmLink,
  useCreateLink,
  useHeldCode,
  useRemoveLink,
  type Link,
  type MessagingApp,
} from "./api";
import { useServerSeconds } from "./clock";
import { ImessageMark } from "@crew/ui/components/imessage-mark";
import { ContactCard, QrCode, WaitingArc } from "./parts";
import {
  countdown,
  defaultCountry,
  linkErrorHint,
  numberFor,
  codeLine,
  numberLine,
  refusedHint,
  shownHandle,
  windowStep,
} from "./view";

/** What opens the window: a new link (or a relink), or the link a row shows. */
export type WindowOpen = { linkId: string | null };

export function LinkWindow({
  open,
  app,
  timezone,
  onClose,
}: {
  open: WindowOpen | null;
  app: MessagingApp | undefined;
  timezone: string;
  onClose: () => void;
}) {
  const mobile = useIsMobile();
  return (
    <WindowShell open={open !== null} mobile={mobile} onClose={onClose}>
      {open && app ? (
        <LinkSteps
          key={open.linkId ?? "new"}
          start={open.linkId}
          app={app}
          timezone={timezone}
          mobile={mobile}
          onClose={onClose}
        />
      ) : null}
    </WindowShell>
  );
}

/** The window's steps, apart from its frame so a test can render them. */
export function LinkSteps({
  start,
  app,
  timezone,
  mobile,
  onClose,
}: {
  start: string | null;
  app: MessagingApp;
  timezone: string;
  mobile: boolean;
  onClose: () => void;
}) {
  const [linkId, setLinkId] = useState(start);
  const now = useServerSeconds(linkId !== null);
  const step = linkId ? windowStep(app, linkId, now) : null;
  const create = useCreateLink();
  const again = (number: string) =>
    create.mutate({ app: app.app, number }, { onSuccess: ({ link }) => setLinkId(link.id) });

  // None yet; or cancelled elsewhere, declined or tidied: back to the number.
  if (!linkId || !step || step === "gone") {
    return (
      <NumberStep
        app={app}
        timezone={timezone}
        mobile={mobile}
        notice={
          step === "gone" ? "That code isn't waiting any more. Start again with your number." : null
        }
        onCancel={onClose}
        onMade={setLinkId}
      />
    );
  }
  if (step === "linked") {
    const linked = app.linked?.id === linkId ? app.linked : app.link;
    return linked ? <LinkedStep link={linked} mobile={mobile} onClose={onClose} /> : null;
  }
  const link = app.link as Link;
  return (
    <CodeStep
      link={link}
      step={step}
      now={now}
      mobile={mobile}
      busy={create.isPending}
      error={create.error}
      onNewCode={() => again(link.number)}
      onClose={onClose}
    />
  );
}

// ── 1. Your number ──────────────────────────────────────────────────────

function CountryPicker({
  country,
  onChange,
  mobile,
}: {
  country: Country;
  onChange: (c: Country) => void;
  mobile: boolean;
}) {
  return (
    <label
      className={cn(
        "relative flex shrink-0 items-center gap-1.5 rounded-md border border-input bg-card text-foreground focus-within:ring-3 focus-within:ring-ring/50",
        mobile ? "p-3 text-base leading-5" : "px-3 py-2.5 text-body leading-5",
      )}
    >
      <span>
        {country.iso} +{country.code}
      </span>
      <ChevronDown aria-hidden className="size-3 text-muted-foreground" />
      <select
        aria-label="Country"
        value={country.iso}
        onChange={(e) => {
          const next = COUNTRIES.find((c) => c.iso === e.target.value);
          if (next) onChange(next);
        }}
        className="absolute inset-0 cursor-pointer opacity-0"
      >
        {COUNTRIES.map((c) => (
          <option key={c.iso} value={c.iso}>
            {c.iso} +{c.code}
          </option>
        ))}
      </select>
    </label>
  );
}

const NEXT = "Next, you'll text us a code to prove it's yours.";

/** The number, as typed: its placeholder in the chosen country's way. */
function NumberInput({
  value,
  onChange,
  country,
  mobile,
  invalid,
}: {
  value: string;
  onChange: (value: string) => void;
  country: Country;
  mobile: boolean;
  invalid: boolean;
}) {
  return (
    <Input
      id="link-number"
      type="tel"
      inputMode="tel"
      autoComplete="tel"
      autoFocus
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={country.code === "1" ? "(226) 224-6894" : `+${country.code}`}
      aria-invalid={invalid || undefined}
      className={cn(
        "h-auto flex-1 rounded-md bg-card focus-visible:border-[1.5px] focus-visible:border-foreground",
        mobile ? "p-3 text-base leading-5" : "px-3 py-2.5 text-body leading-5",
      )}
    />
  );
}

/** Under the field: why the number was refused, else its line. */
function FieldNote({ error, line }: { error: string | null; line: string }) {
  if (error) {
    return (
      <p role="alert" className="text-meta leading-4.5 text-destructive">
        {error}
      </p>
    );
  }
  return <p className="text-meta leading-4.5 text-muted-foreground">{line}</p>;
}

/** Continue, and what comes next; Cancel beside it on a desktop. */
function NumberFooter({
  mobile,
  busy,
  onCancel,
}: {
  mobile: boolean;
  busy: boolean;
  onCancel: () => void;
}) {
  if (mobile) {
    return (
      <div className="flex flex-col gap-2.5 px-5 pt-4.5 pb-8.5">
        <Button type="submit" size="lg" className={BIG} disabled={busy}>
          Continue
        </Button>
        <p className="text-center text-meta leading-4.5 text-hint">{NEXT}</p>
      </div>
    );
  }
  return (
    <Footer line={<span className="text-meta leading-4.5 text-hint">{NEXT}</span>}>
      <Button type="button" variant="ghost" size="lg" onClick={onCancel}>
        Cancel
      </Button>
      <Button type="submit" size="lg" disabled={busy}>
        Continue
      </Button>
    </Footer>
  );
}

function NumberStep({
  app,
  timezone,
  mobile,
  notice,
  onCancel,
  onMade,
}: {
  app: MessagingApp;
  timezone: string;
  mobile: boolean;
  notice: string | null;
  onCancel: () => void;
  onMade: (linkId: string) => void;
}) {
  const [country, setCountry] = useState(() => defaultCountry(timezone));
  const [input, setInput] = useState("");
  const create = useCreateLink();
  // A national number that can't be made E.164 in the country chosen: refused here.
  const [refused, setRefused] = useState<string | null>(null);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;
    const number = numberFor(input, country);
    setRefused(number ? null : refusedHint(country, input));
    if (!number) return create.reset();
    create.mutate({ app: app.app, number }, { onSuccess: ({ link }) => onMade(link.id) });
  };
  const error = refused ?? (create.error ? linkErrorHint(create.error, country, input) : null);
  const hint = mobile
    ? "The number this iPhone texts from. Only you see it."
    : "Use the number your iPhone texts from. Only you see it.";
  return (
    <form onSubmit={submit} data-link-step="number" className="flex flex-col">
      <Head
        mobile={mobile}
        eyebrow="Step 1 of 2"
        title={app.linked ? "Relink iMessage" : "Link iMessage"}
        line={numberLine(app.linked, mobile)}
      />
      <div className={cn("flex flex-col gap-2", mobile ? "px-5 pt-4.5" : "px-7 py-6")}>
        <label htmlFor="link-number" className="text-sm leading-5 font-medium text-foreground">
          Your mobile number
        </label>
        <div className="flex gap-2">
          <CountryPicker country={country} onChange={setCountry} mobile={mobile} />
          <NumberInput
            value={input}
            onChange={setInput}
            country={country}
            mobile={mobile}
            invalid={Boolean(create.error)}
          />
        </div>
        <FieldNote error={error} line={notice ?? hint} />
      </div>
      <NumberFooter mobile={mobile} busy={create.isPending} onCancel={onCancel} />
    </form>
  );
}

// ── 2. Text the code ────────────────────────────────────────────────────

/** "Is that you?" in place of the countdown (spec §6.1): Yes links it, No refuses that handle. */
function ConfirmQuestion({
  link,
  mobile,
  onDeclined,
}: {
  link: Link;
  mobile: boolean;
  /** No: the person said it wasn't them, so the window closes. */
  onDeclined: () => void;
}) {
  const confirm = useConfirmLink();
  const answer = (yes: boolean) =>
    confirm.mutate({ id: link.id, yes }, { onSuccess: () => !yes && onDeclined() });
  return (
    <div
      data-link-confirm
      className={cn("flex flex-col gap-2.5", !mobile && "flex-1 md:flex-row md:items-center")}
    >
      <p className={cn("text-sm leading-5 text-copy", !mobile && "flex-1")}>
        We got your code from <span className="font-medium text-foreground">{link.handle}</span>. Is
        that you?
        {confirm.error ? (
          <span role="alert" className="block text-meta text-destructive">
            {errorHint(confirm.error)}
          </span>
        ) : null}
      </p>
      <div className={cn("flex gap-2", mobile && "flex-col")}>
        {mobile ? null : (
          <Button
            variant="ghost"
            size="lg"
            disabled={confirm.isPending}
            onClick={() => answer(false)}
          >
            No
          </Button>
        )}
        <Button
          size="lg"
          className={mobile ? BIG : undefined}
          disabled={confirm.isPending}
          onClick={() => answer(true)}
        >
          Yes, that's me
        </Button>
        {mobile ? (
          <Button
            variant="outline"
            size="lg"
            className={cn(BIG, "bg-card")}
            disabled={confirm.isPending}
            onClick={() => answer(false)}
          >
            No
          </Button>
        ) : null}
      </div>
    </div>
  );
}

/** The code, large, and where it goes (Paper 62's panel). */
function CodePanel({ link, code, mobile }: { link: Link; code: string | null; mobile: boolean }) {
  const line = shownHandle(link.line);
  const shown = code ?? "CREW-······";
  if (mobile) {
    return (
      <div className="mx-5 mt-4.5 flex flex-col gap-0.5 rounded-lg bg-background p-4">
        <span
          data-link-code={code ?? undefined}
          className="font-mono text-title leading-9 font-medium tracking-[0.04em] text-foreground"
        >
          {shown}
        </span>
        <span className="text-sm leading-5 text-muted-foreground">to {line}</span>
      </div>
    );
  }
  return (
    <div className="flex flex-1 basis-0 flex-col justify-center gap-1 rounded-lg bg-background p-5">
      <span className="text-meta leading-4.5 text-muted-foreground">Send</span>
      <span
        data-link-code={code ?? undefined}
        className="font-mono text-[34px] leading-10 font-medium tracking-[0.04em] text-foreground"
      >
        {shown}
      </span>
      <span className="pt-1.5 text-meta leading-4.5 text-muted-foreground">to</span>
      <span className="text-question leading-6 font-semibold text-foreground">{line}</span>
    </div>
  );
}

/** What step 2 is showing: the code, a code this tab no longer holds, or why it stopped. */
type CodeMode = "code" | "lost" | "expired" | "confirming";

/** The waiting arc and the countdown (Paper 62, 62b). */
function Waiting({ left, mobile }: { left: string; mobile: boolean }) {
  if (mobile) {
    return (
      <div className="flex items-center justify-center gap-2">
        <WaitingArc size={14} />
        <span className="text-meta leading-4.5 text-muted-foreground">
          Waiting for your text · {left} left
        </span>
      </div>
    );
  }
  return (
    <span className="flex min-w-0 items-center gap-2.5">
      <WaitingArc size={16} />
      <span className="flex min-w-0 flex-col">
        <span className="text-sm leading-5 text-copy">Waiting for your text</span>
        <span className="text-meta leading-4.5 text-hint">Code works for {left}</span>
      </span>
    </span>
  );
}

/** What step 2 does once the code shows, per mode, and Cancel. */
type CodeActions = {
  mode: CodeMode;
  link: Link;
  held: { code: string; sms_url: string } | null;
  left: string;
  busy: boolean;
  cancelling: boolean;
  onNewCode: () => void;
  onCancel: () => void;
  onClose: () => void;
};

/** Opens Messages with the code typed in. Primary on a phone and a desktop: an sms: link works in Messages on both. */
function TextCodeButton({ href, className }: { href: string | undefined; className?: string }) {
  return (
    <a
      href={href}
      className={cn(buttonVariants({ variant: "default", size: "lg" }), "gap-2", className)}
    >
      <ImessageMark size={18} tone="current" />
      Text the code
    </a>
  );
}

/** Step 2 on a phone (Paper 62b): Text the code, then the countdown; or the question, or New code. */
function PhoneCode(a: CodeActions) {
  const action =
    a.mode === "confirming" ? (
      <ConfirmQuestion link={a.link} mobile onDeclined={a.onClose} />
    ) : a.mode === "code" ? (
      <>
        <TextCodeButton href={a.held?.sms_url} className={BIG} />
        <Waiting left={a.left} mobile />
      </>
    ) : (
      <Button size="lg" className={BIG} disabled={a.busy} onClick={a.onNewCode}>
        New code
      </Button>
    );
  return (
    <>
      <CodePanel link={a.link} code={a.mode === "expired" ? null : (a.held?.code ?? null)} mobile />
      <div className="flex flex-col gap-2.5 px-5 pt-4.5 pb-8.5">
        {action}
        <Button
          variant="ghost"
          size="lg"
          className="h-11"
          disabled={a.cancelling}
          onClick={a.onCancel}
        >
          Cancel
        </Button>
      </div>
    </>
  );
}

/** The QR code of the `sms:` link, for the phone to scan (spec §6.2). */
function QrColumn({ smsUrl }: { smsUrl: string }) {
  return (
    <div className="flex w-43 shrink-0 flex-col items-center gap-2.5">
      <a
        href={smsUrl}
        aria-label="Text it"
        className="flex size-38 shrink-0 rounded-lg border border-border bg-white p-2.5"
      >
        <QrCode text={smsUrl} />
      </a>
      <span className="text-center text-xs leading-4 text-muted-foreground">
        Or scan with your phone.
      </span>
    </div>
  );
}

/** Step 2's desktop footer: the question; else the countdown, or why it stopped and New code. */
function DesktopFooter(a: CodeActions) {
  if (a.mode === "confirming") {
    return (
      <div className="flex items-center gap-3 border-t border-border px-7 py-4">
        <ConfirmQuestion link={a.link} mobile={false} onDeclined={a.onClose} />
      </div>
    );
  }
  const waiting = a.mode === "code";
  const stopped = a.mode === "expired" ? "Code expired." : "Code hidden.";
  return (
    <Footer
      line={
        waiting ? (
          <Waiting left={a.left} mobile={false} />
        ) : (
          <span className="text-sm leading-5 text-copy">{stopped}</span>
        )
      }
    >
      <Button variant="ghost" size="lg" disabled={a.cancelling} onClick={a.onCancel}>
        Cancel
      </Button>
      {waiting && a.held ? <TextCodeButton href={a.held.sms_url} /> : null}
      {waiting ? null : (
        <Button size="lg" disabled={a.busy} onClick={a.onNewCode}>
          New code
        </Button>
      )}
    </Footer>
  );
}

/** Step 2 on a desktop (Paper 62): the code beside its QR code, the countdown in the footer. */
function DesktopCode(a: CodeActions) {
  const shown = a.mode === "code" || a.mode === "confirming" ? a.held : null;
  return (
    <>
      <div className="flex gap-6 px-7 py-6">
        <CodePanel
          link={a.link}
          code={a.mode === "expired" ? null : (a.held?.code ?? null)}
          mobile={false}
        />
        {shown ? <QrColumn smsUrl={shown.sms_url} /> : null}
      </div>
      <DesktopFooter {...a} />
    </>
  );
}

function CodeStep({
  link,
  step,
  now,
  mobile,
  busy,
  error,
  onNewCode,
  onClose,
}: {
  link: Link;
  step: "code" | "expired" | "confirming";
  now: number;
  mobile: boolean;
  busy: boolean;
  error: unknown;
  onNewCode: () => void;
  onClose: () => void;
}) {
  const held = useHeldCode(link.id);
  const remove = useRemoveLink();
  // After a reload the code isn't held: a new one is a click away.
  const mode: CodeMode = step === "code" && !held ? "lost" : step;
  const problem = error ?? remove.error;
  const actions: CodeActions = {
    mode,
    link,
    held,
    left: countdown(link.expires_at, now),
    busy,
    cancelling: remove.isPending,
    onNewCode,
    onCancel: () => remove.mutate(link.id, { onSuccess: onClose }),
    onClose,
  };
  return (
    <div data-link-step={step} className="flex flex-col">
      <Head mobile={mobile} eyebrow="Step 2 of 2" title="Text this code" line={codeLine(mode)} />
      {problem ? (
        <p role="alert" className={cn("pt-3 text-meta text-destructive", mobile ? "px-5" : "px-7")}>
          {errorHint(problem)}
        </p>
      ) : null}
      {mobile ? <PhoneCode {...actions} /> : <DesktopCode {...actions} />}
    </div>
  );
}

// ── 3. Linked ───────────────────────────────────────────────────────────

function LinkedStep({
  link,
  mobile,
  onClose,
}: {
  link: Link;
  mobile: boolean;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const openThread = () => {
    onClose();
    if (link.thread_id) void navigate(`/chats/${link.thread_id}`);
  };
  const handle = shownHandle(link.handle ?? link.number);
  const line = shownHandle(link.line);
  if (mobile) {
    return (
      <div data-link-step="linked" className="flex flex-col">
        <Head
          mobile
          eyebrow="Linked"
          tone="moss"
          title="You can text your crew now"
          line="One last tap: we've texted you Crew's contact card. Add it, so your crew's texts show as Crew, not a number."
        />
        <ContactCard className="mx-5 mt-4.5 w-60 self-start" />
        <div className="flex flex-col gap-2.5 px-5 pt-4.5 pb-8.5">
          <a href={`sms:${link.line}`} className={cn(buttonVariants({ size: "lg" }), BIG, "gap-2")}>
            <ImessageMark size={18} tone="current" />
            Open Messages to add it
          </a>
          <Button variant="outline" size="lg" className={cn(BIG, "bg-card")} onClick={onClose}>
            Done
          </Button>
        </div>
      </div>
    );
  }
  return (
    <div data-link-step="linked" className="flex flex-col">
      <Head
        mobile={false}
        eyebrow="Linked"
        tone="moss"
        title="You can text your crew now"
        line={`Linked to ${handle}. Text ${line} anytime.`}
      />
      <div className="flex items-center gap-5 px-7 py-6">
        <ContactCard className="w-57" />
        <div className="flex flex-1 flex-col gap-1.5">
          <span className="text-body leading-5.5 font-semibold text-foreground">
            One last tap, on your iPhone
          </span>
          <span className="text-sm leading-5.25 text-copy">
            We've texted you Crew's contact card. Add it, so your crew's texts show as Crew, with
            its mark, instead of a number.
          </span>
        </div>
      </div>
      <div className="flex items-center justify-end gap-2 border-t border-border px-7 py-4">
        {link.thread_id ? (
          <Button variant="outline" size="lg" onClick={openThread}>
            Open thread
          </Button>
        ) : null}
        <Button size="lg" onClick={onClose}>
          Done
        </Button>
      </div>
    </div>
  );
}
